import React, { useState, useEffect } from 'react';
import './Profile.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function Profile({ username, currentUser, onClose, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteAllPostsConfirm, setShowDeleteAllPostsConfirm] = useState(false);
  const [verificationRequested, setVerificationRequested] = useState(false);
  const [requestingVerification, setRequestingVerification] = useState(false);

  // username prop is displayName
  const displayName = username;
  const isOwnProfile = currentUser && currentUser.displayName === displayName;

  useEffect(() => {
    fetchProfile();
    fetchUserPosts();
  }, [displayName]);

  useEffect(() => {
    // Set verification requested status from current user if it's own profile
    if (isOwnProfile && currentUser) {
      setVerificationRequested(currentUser.verificationRequested || false);
    }
  }, [isOwnProfile, currentUser]);

  const fetchProfile = async () => {
    try {
      const encodedDisplayName = encodeURIComponent(displayName);
      const url = `${API_URL}/api/users/${encodedDisplayName}`;
      console.log('🔍 Fetching profile...');
      console.log('DisplayName:', displayName);
      console.log('Encoded:', encodedDisplayName);
      console.log('Full URL:', url);

      const response = await fetch(url);
      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Profile found:', data.user?.displayName);
        setProfile(data.user);
        setBio(data.user.bio || '');
        setLocation(data.user.location || '');
        setProfilePicture(data.user.profilePicture || '');
        // Set verification requested status if it's own profile
        if (isOwnProfile && data.user.verificationRequested) {
          setVerificationRequested(true);
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Error fetching profile:', errorData);
        console.error('Tried to fetch displayName:', displayName);
        console.error('Response status:', response.status);
      }
    } catch (error) {
      console.error('❌ Exception fetching profile:', error);
      console.error('Tried to fetch displayName:', displayName);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${encodeURIComponent(displayName)}/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching user posts:', error);
      console.error('Tried to fetch posts for displayName:', displayName);
    }
  };

  const handleVerificationRequest = async () => {
    if (!window.confirm('Request verification for access to Discord and WhatsApp? This will send your information to the site administrator.')) {
      return;
    }

    setRequestingVerification(true);
    try {
      const response = await fetch(`${API_URL}/api/users/verification-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setVerificationRequested(true);
        // Update current user object if available
        if (currentUser) {
          currentUser.verificationRequested = true;
          currentUser.verificationRequestedAt = data.verificationRequestedAt;
        }
        alert(data.message || 'Verification request submitted successfully! You will be contacted via email.');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to send verification request');
      }
    } catch (error) {
      console.error('Error sending verification request:', error);
      alert('Failed to send verification request. Please try again later.');
    } finally {
      setRequestingVerification(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bio, location, profilePicture: profilePicture || '' })
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data.user);
        alert('Profile updated successfully!');
        // Keep form visible - don't hide it
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/account`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Account deleted successfully');
        onLogout();
        onClose();
      } else {
        alert('Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account');
    }
  };

  const handleDeleteAllPosts = async () => {
    if (posts.length === 0) return;

    const deleteLoading = true;
    try {
      // Delete all posts one by one
      const deletePromises = posts.map(post => 
        fetch(`${API_URL}/api/posts/${post.id}`, {
          method: 'DELETE',
          credentials: 'include'
        })
      );

      const results = await Promise.all(deletePromises);
      const failed = results.filter(r => !r.ok).length;

      if (failed === 0) {
        setPosts([]);
        setShowDeleteAllPostsConfirm(false);
        // Refresh the profile to update post count
        fetchUserPosts();
      } else {
        alert(`Failed to delete ${failed} post(s). Please try again.`);
      }
    } catch (error) {
      console.error('Error deleting posts:', error);
      alert('Failed to delete posts');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="profile-modal">
        <div className="profile-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-modal">
        <div className="profile-content">
          <button className="modal-close" onClick={onClose}>×</button>
          <p>User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-modal" onClick={onClose}>
      <div className="profile-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <div className="profile-header">
          <div className="profile-info">
            <h2>
              {profile.displayName}
              {profile.verified && (
                <span className="verified-badge verified-badge-large" title="Verified user">✓</span>
              )}
            </h2>
            <p className="join-date">Joined {formatDate(profile.createdAt)}</p>
          </div>
        </div>

        {isOwnProfile ? (
          <form onSubmit={handleUpdateProfile} className="profile-edit-form">
            <div className="verification-request-section">
              <button
                type="button"
                onClick={handleVerificationRequest}
                disabled={verificationRequested || requestingVerification}
                className="btn-verification-request"
              >
                {requestingVerification ? 'Sending...' : verificationRequested ? 'Request Sent ✓' : 'Request verification for access to Discord and WhatsApp'}
              </button>
              <p className="verification-request-note">
                This will request a manual review from the site admin. Further correspondence will be done over email.
              </p>
            </div>
            <div className="form-group">
              <label>Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                rows="4"
                maxLength="500"
              />
              <small>{bio.length}/500</small>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Where are you from?"
                maxLength="100"
              />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={saving || loading}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                type="button" 
                onClick={() => setShowDeleteConfirm(true)} 
                className="btn-delete-account"
                disabled={saving || loading}
              >
                Delete Account
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-details">
            {profile.bio && (
              <div className="profile-section">
                <h3>Bio</h3>
                <p>{profile.bio}</p>
              </div>
            )}
            {profile.location && (
              <div className="profile-section">
                <h3>Location</h3>
                <p>{profile.location}</p>
              </div>
            )}
          </div>
        )}

        <div className="profile-posts">
          <div className="profile-posts-header">
            <h3>Posts ({posts.length})</h3>
            {isOwnProfile && posts.length > 0 && (
              <button
                onClick={() => setShowDeleteAllPostsConfirm(true)}
                className="btn-delete-all-posts"
                disabled={loading}
              >
                Delete All Posts
              </button>
            )}
          </div>
          {posts.length === 0 ? (
            <p className="no-posts">No posts yet</p>
          ) : (
            <div className="posts-list">
              {posts.map((post) => (
                <div key={post.id} className="post-item">
                  <h4 className="post-item-title">{post.title}</h4>
                  {post.type === 'text' && post.content && (
                    <div className="post-content">{post.content}</div>
                  )}
                  {post.type === 'image' && post.url && (
                    <div className="post-link">
                      <a href={post.url} target="_blank" rel="noopener noreferrer" className="external-link">
                        {post.url}
                      </a>
                    </div>
                  )}
                  {post.type === 'link' && post.url && (
                    <div className="post-link">
                      <a href={post.url} target="_blank" rel="noopener noreferrer" className="external-link">
                        {post.url}
                      </a>
                    </div>
                  )}
                  <div className="post-meta">
                    <span>{formatDate(post.timestamp)}</span>
                    <span>▲ {post.upvoteCount || 0}</span>
                    <span>{post.replies.length} replies</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm">
              <h3>Delete Account?</h3>
              <p>This action cannot be undone. All your posts and replies will be permanently deleted.</p>
              <div className="confirm-actions">
                <button onClick={handleDeleteAccount} className="btn-confirm-delete">
                  Yes, Delete My Account
                </button>
                <button onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteAllPostsConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm">
              <h3>Delete All Posts?</h3>
              <p>Are you sure you want to delete all {posts.length} of your posts? This action cannot be undone.</p>
              <div className="confirm-actions">
                <button onClick={handleDeleteAllPosts} className="btn-confirm-delete">
                  Yes, Delete All Posts
                </button>
                <button onClick={() => setShowDeleteAllPostsConfirm(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
